import type { FieldSchemaModel, HiddenOnContext } from '@/shared/generated'

import React, { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { CheckIcon, SettingsIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import {
	FormProvider,
	RHFCheckbox,
	RHFSelect,
	RHFTextField,
} from '@/shared/components/HookForm'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTrigger,
} from '@/shared/components/ui/modal'
import { FieldType, ValueEditorType, ValueType } from '@/shared/generated'
import { cn } from '@/shared/lib/utils'

import { useColumnsSchemaQuery } from '../api/queries'

const FieldSchema = z.object({
	id: z.string().min(1, 'Id is required'),
	name: z.string().min(1, 'Name is required'),
	description: z.string().nullable(),
	value_type: z.string().min(1, 'Value type is required'),
	value_editor_type: z.string().nullable(),
	field_type: z.string().min(1, 'Field type is required'),
	reference: z.any().nullable(),
	options: z.any(),
	params: z.any(),
})
type FormData = z.infer<typeof FieldSchema>

export default function FieldsEditorModal({ name }: { name: HiddenOnContext }) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const { data: columnsSchema } = useColumnsSchemaQuery(name)
	const [fields, setFields] = useState<FieldSchemaModel[]>([])
	useEffect(() => {
		setFields(columnsSchema || [])
	}, [columnsSchema])
	const [selectedField, setSelectedField] = useState<FormData | null>(null)
	const [open, setOpen] = useState(false)

	return (
		<Modal open={open} onOpenChange={setOpen}>
			<ModalTrigger asChild>
				<Button size='icon'>
					<SettingsIcon />
				</Button>
			</ModalTrigger>
			<ModalContent className='max-w-6xl'>
				<ModalHeader>
					<ModalTitle>Fields editor</ModalTitle>
				</ModalHeader>
				<ModalBody className='-mx-4 flex h-full max-h-[500px] gap-4 overflow-hidden'>
					<div className='flex w-2/3 flex-col overflow-y-auto'>
						<Command className='rounded-lg border bg-secondary/30'>
							<CommandInput placeholder='Search field' />
							<CommandList className='max-h-auto'>
								<CommandEmpty>
									<p>No field found</p>
								</CommandEmpty>
								<CommandGroup>
									{fields.map((field) => (
										<CommandItem
											key={field.id}
											value={field.id}
											onSelect={() => setSelectedField(field as any)}
										>
											<p className='truncate'>
												{t(field.name, { defaultValue: field.name })}
											</p>
											<div className='ml-auto flex items-center gap-2'>
												<Badge
													variant={field.field_type ? 'success' : 'neutral'}
												>
													{field.field_type || 'none'}
												</Badge>
												<CheckIcon
													className={cn(
														selectedField?.id === field.id
															? 'opacity-100'
															: 'opacity-0'
													)}
												/>
											</div>
										</CommandItem>
									))}
								</CommandGroup>
							</CommandList>
						</Command>
					</div>
					<div className='flex w-1/3 flex-col overflow-y-auto'>
						{selectedField && (
							<FieldEditorForm
								key={selectedField.id}
								defaultValues={selectedField}
								onSubmit={(data) => {
									setFields(
										fields.map((field) =>
											field.id === data.id ? { ...field, ...data } : field
										) as FieldSchemaModel[]
									)
									setSelectedField(null)
								}}
							/>
						)}
					</div>
				</ModalBody>
				<ModalFooter>
					<ModalClose asChild>
						<Button variant='outline'>Cancel</Button>
					</ModalClose>
					<Button
						variant='primary'
						onClick={() => {
							queryClient.setQueryData(['schema', name], fields)
							setOpen(false)
						}}
					>
						Apply
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	)
}

function FieldEditorForm({
	defaultValues,
	onSubmit,
}: {
	defaultValues: FormData
	onSubmit: (data: FormData) => void
}) {
	const { t } = useTranslation()

	const methods = useForm<FormData>({
		defaultValues,
		resolver: zodResolver(FieldSchema),
	})
	const valueType = methods.watch('value_type')
	const valueEditorTypeOptions = Object.values(ValueEditorType).map((type) => ({
		label: t(type, { defaultValue: type }),
		value: type,
	}))
	const fieldTypeOptions = Object.values(FieldType)
		.map((type) => ({
			label: t(type, { defaultValue: type }),
			value: type,
		}))
		.filter((option) => {
			if (valueType === ValueType.Number || valueType === ValueType.Integer) {
				return [
					FieldType.Number,
					FieldType.Currency,
					FieldType.Price,
					FieldType.Custom,
					FieldType.Delta,
					FieldType.Percent,
				].includes(option.value)
			}
			if (valueType === ValueType.Array) {
				return [
					FieldType.Count,
					FieldType.Chip,
					FieldType.Choice,
					FieldType.Badge,
					FieldType.Custom,
					FieldType.Tree,
				].includes(option.value)
			}
			if (valueType === ValueType.Boolean) {
				return [
					FieldType.Checkbox,
					FieldType.Flag,
					FieldType.Lock,
					FieldType.Custom,
				].includes(option.value)
			}

			return true
		})

	return (
		<FormProvider
			methods={methods}
			className='flex flex-col gap-4'
			onSubmit={methods.handleSubmit(onSubmit)}
		>
			<RHFTextField name='name' label='Name' />
			<RHFTextField name='description' label='Description' />
			<div className='grid grid-cols-2 gap-4'>
				<RHFSelect
					className='w-full'
					name='value_type'
					disabled
					label='Value type'
					options={[
						{ label: 'String', value: 'string' },
						{ label: 'Integer', value: 'integer' },
						{ label: 'Number', value: 'number' },
						{ label: 'Boolean', value: 'boolean' },
						{ label: 'Array', value: 'array' },
						{ label: 'Date', value: 'date' },
						{ label: 'Datetime', value: 'datetime' },
					]}
				/>
				<RHFSelect
					className='w-full'
					name='value_editor_type'
					label='Value editor type'
					options={valueEditorTypeOptions}
				/>
			</div>
			<RHFSelect
				className='w-full'
				name='field_type'
				label='Field type'
				options={fieldTypeOptions}
			/>
			<RHFCheckbox name='params.sortable' label='Sortable' />
			<RHFCheckbox name='params.editable' label='Editable' />
			<RHFCheckbox name='params.searchable' label='Searchable' />
			<RHFCheckbox name='params.filterable' label='Filterable' />
			<RHFCheckbox name='params.locakble' label='Locakble' />
			<RHFCheckbox name='params.locked' label='Locked' />
			<div>
				<Button type='submit' variant='primary'>
					Save
				</Button>
			</div>
		</FormProvider>
	)
}
